import type { TriggerEventStatus } from './../../reducers/workflows/index';
import {
  selectIsMobileSDK,
  selectWindowUpdatedContextVariables,
} from './../../reducers/workflows/index';
import { createAction } from '@reduxjs/toolkit';
import lazyLogRocket from 'lazy-logrocket';
import { UAParser } from 'ua-parser-js';
//Redux
import { selectIsAutonomousAgent, selectRawParams } from 'reducers/config';
import type { HandoffStatus } from 'reducers/workflows';

//Types
import type {
  ArticleComponent,
  AttemptedToFetchRedactedConversationError,
  ContextVariables,
  CsatConfig,
  CsatFormData,
  HandoffIntegrations,
  InitConversationInterface,
  NewIrArticleComponent,
  SendTrackingEventParameters,
  TriggerEventStepContextVariable,
  WidgetMode,
  WorkflowConfigArgs,
  WorkflowConfigResponse,
} from 'api/types';
import { isAttemptedToFetchRedactedConversationError } from 'api/types';

// Util
import ApiService from '../../services/ApiService';
import { isIgnorableNetworkError } from 'api/utils';
import { postMessage } from 'utils/postMessage';
import { selectIsCurrentTabPrimary } from 'persistence/persistenceBetweenTabs';
import { CustomFetchError, rejectWithCustomFetchError } from 'api/customFetch';
import { captureException, getCurrentScope } from '@sentry/react';
import type { ReadonlyDeep } from 'type-fest';
import { omit } from 'persistence/utils';
import { createAppAsyncThunk } from 'createAppAsyncThunk';
import { sentryHandoffErrorIgnoreList } from 'constants/sentry';

export const WorkflowsActions = {
  ADD_RAW_PARAMS: 'workflows/setRawParams',
  CLEAN_CONVERSATION: 'workflows/clean_conversation',
  CLEAN_TRIGGER_EVENT: 'workflows/cleanTriggerEvent',
  CLEAN_WIDGET_COMPONENTS: 'workflows/clean_widget_components',
  CONFIG_WIDGET: 'workflows/configWidget',
  RELOAD_TRANSLATED_WIDGET_CONFIG: 'workflows/reloadTranslatedWidgetConfig',
  RESET_FEEDBACK: 'workflows/resetFeedback',
  RESET_PREVIEW_WIDGET_CONFIG: 'workflows/resetPreviewWidgetConfig',
  RESTART_CONVERSATION: 'workflows/restartConversation',
  SEND_TRACKING_EVENT: 'workflows/sendTrackingEvent',
  SET_CSAT_DETAIL_VISIBILITY: 'workflows/setCsatDetailVisibility',
  SET_CSAT_FEEDBACK: 'workflows/setCsatFeedback',
  SET_CSAT_RESOLVE: 'workflows/setCsatResolve',
  SET_CSAT_SCORE: 'workflows/setCsatScore',
  SET_CSAT_TYPE: 'workflows/setCsatType',
  SET_CSAT_VISIBILITY: 'workflows/setCsatVisibility',
  SET_CUSTOM_CONFIG_PARAMS: 'workflows/setCustomConfigParams',
  SET_CUSTOM_PARAMS: 'workflows/setCustomParams',
  SET_DID_SUBMIT: 'workflows/setDidSubmit',
  SET_DID_USER_CLOSE_PROACTIVE_CHAT: 'workflows/setDidUserCloseProactiveChat',
  SET_HANDOFF_INTEGRATION: 'workflows/handoffIntegration',
  SET_HANDOFF_STATUS: 'workflows/handoffStatus',
  SET_INITIAL_INTENT_ID: 'workflows/setInitialIntentId',
  SET_INITIAL_QUERY: 'workflows/setInitialQuery',
  SET_INTENT_TITLE: 'workflows/setIntentTitles',
  SET_IS_BUILDER_PREVIEW: 'workflows/setIsBuilderPreview',
  SET_IS_CSAT_DATA_SUBMITTED: 'workflows/setIsCsatDataSubmitted',
  SET_IS_CSAT_END_TRANSITION: 'workflows/setIsCsatEndTransition',
  SET_IS_CSAT_SURVEY_FINISHED: 'workflows/setIsCsatSurveyFinished',
  SET_IS_MODEL_TRAINING: 'workflows/setIsModelTraining',
  SET_IS_SOLVE_CONFIG_PREVIEW: 'workflows/setIsSolveConfigPreview',
  SET_PARENT_URL: 'workflows/setParentUrl',
  SET_SELECTED_ARTICLE: 'workflows/selectedArticle',
  SET_SELECTED_INTENT_INDEX: 'workflows/setSelectedIntentIndex',
  SET_SHOULD_INIT_LOG_ROCKET: 'workflows/setLogRocketInitialized',
  SET_STEP_CONTEXT_VARIABLES: 'workflows/setStepContextVariables',
  SET_STEP_FILE_NAMES: 'workflows/setStepFileNames',
  SET_STEP_SENT: 'workflows/setStepSent',
  SET_TRIGGER_EVENT_EXPECTED_CONTEXT_VARIABLES:
    'workflows/setTriggerEventExpectedContextVariables',
  SET_TRIGGER_EVENT_EXPECTED_EVENT_ID:
    'workflows/setTriggerEventExpectedEventId',
  SET_TRIGGER_EVENT_EXPECTED_EVENT_NAME:
    'workflows/setTriggerEventExpectedEventName',
  SET_TRIGGER_EVENT_PAYLOAD_CONTEXT_VARIABLES:
    'workflows/setTriggerEventPayloadContextVariables',
  SET_TRIGGER_EVENT_PAYLOAD_EVENT_NAME:
    'workflows/setTriggerEventPayloadEventName',
  SET_TRIGGER_EVENT_STATUS: 'workflows/setTriggerEventStatus',
  SET_WIDGET_MODE: 'workflows/setWidgetMode',
  SET_WORKFLOW_VERSION: 'workflows/setWorkflowVersion',
} as const;

export const setCsatFeedback = createAction<string[]>(
  WorkflowsActions.SET_CSAT_FEEDBACK,
);

export const setCsatResolve = createAction<boolean | undefined>(
  WorkflowsActions.SET_CSAT_RESOLVE,
);

export const setCsatDetailVisibility = createAction<boolean>(
  WorkflowsActions.SET_CSAT_DETAIL_VISIBILITY,
);

export const resetFeedback = createAction(WorkflowsActions.RESET_FEEDBACK);

export const setCsatScore = createAction<CsatFormData['csatScore']>(
  WorkflowsActions.SET_CSAT_SCORE,
);

export const setCsatVisibility = createAction<boolean>(
  WorkflowsActions.SET_CSAT_VISIBILITY,
);

export const setCsatType = createAction<CsatConfig['csatType']>(
  WorkflowsActions.SET_CSAT_TYPE,
);

export const setDidSubmit = createAction<CsatConfig['didSubmit']>(
  WorkflowsActions.SET_DID_SUBMIT,
);

export const setSelectedIntentIndex = createAction<number>(
  WorkflowsActions.SET_SELECTED_INTENT_INDEX,
);

export const setIsCsatDataSubmitted = createAction<boolean>(
  WorkflowsActions.SET_IS_CSAT_DATA_SUBMITTED,
);

export const setIsCsatSurveyFinished = createAction<boolean>(
  WorkflowsActions.SET_IS_CSAT_SURVEY_FINISHED,
);

export const setIsCsatEndTransition = createAction<boolean>(
  WorkflowsActions.SET_IS_CSAT_END_TRANSITION,
);

export const setDidUserCloseProactiveChat = createAction<boolean>(
  WorkflowsActions.SET_DID_USER_CLOSE_PROACTIVE_CHAT,
);

export const setCustomParams = createAction<ContextVariables>(
  WorkflowsActions.SET_CUSTOM_PARAMS,
);

export const setIntentTitle = createAction<string>(
  WorkflowsActions.SET_INTENT_TITLE,
);

export const setIsModelTraining = createAction<boolean>(
  WorkflowsActions.SET_IS_MODEL_TRAINING,
);

export const setCustomConfigParams = createAction<ContextVariables>(
  WorkflowsActions.SET_CUSTOM_CONFIG_PARAMS,
);

export const setStepSent = createAction<{
  query?: string;
  stepIndex?: number;
}>(WorkflowsActions.SET_STEP_SENT);

export const setStepFileNames = createAction<{
  fileNames?: Array<string>;
  stepIndex: number;
}>(WorkflowsActions.SET_STEP_FILE_NAMES);

export const setHandoffStatus = createAction<HandoffStatus>(
  WorkflowsActions.SET_HANDOFF_STATUS,
);

export const setTriggerEventStatus = createAction<TriggerEventStatus>(
  WorkflowsActions.SET_TRIGGER_EVENT_STATUS,
);
export const setTriggerEventPayloadContextVariables =
  createAction<ContextVariables>(
    WorkflowsActions.SET_TRIGGER_EVENT_PAYLOAD_CONTEXT_VARIABLES,
  );
export const setTriggerEventPayloadEventName = createAction<string>(
  WorkflowsActions.SET_TRIGGER_EVENT_PAYLOAD_EVENT_NAME,
);
export const setTriggerEventExpectedContextVariables = createAction<
  TriggerEventStepContextVariable[]
>(WorkflowsActions.SET_TRIGGER_EVENT_EXPECTED_CONTEXT_VARIABLES);
export const setTriggerEventExpectedEventName = createAction<string>(
  WorkflowsActions.SET_TRIGGER_EVENT_EXPECTED_EVENT_NAME,
);
export const setTriggerEventExpectedEventId = createAction<string>(
  WorkflowsActions.SET_TRIGGER_EVENT_EXPECTED_EVENT_ID,
);
export const cleanTriggerEvent = createAction(
  WorkflowsActions.CLEAN_TRIGGER_EVENT,
);

export const setHandoffIntegration = createAction<HandoffIntegrations>(
  WorkflowsActions.SET_HANDOFF_INTEGRATION,
);

export const setSelectedArticle = createAction<
  ArticleComponent | NewIrArticleComponent | undefined
>(WorkflowsActions.SET_SELECTED_ARTICLE);

export const cleanConversation = createAction(
  WorkflowsActions.CLEAN_CONVERSATION,
);

export const setIsBuilderPreview = createAction<boolean>(
  WorkflowsActions.SET_IS_BUILDER_PREVIEW,
);

export const setIsSolveConfigPreview = createAction<boolean>(
  WorkflowsActions.SET_IS_SOLVE_CONFIG_PREVIEW,
);

export const setInitialIntentId = createAction<string>(
  WorkflowsActions.SET_INITIAL_INTENT_ID,
);

export const setInitialQuery = createAction<string>(
  WorkflowsActions.SET_INITIAL_QUERY,
);

export const setWorkflowVersion = createAction<string>(
  WorkflowsActions.SET_WORKFLOW_VERSION,
);

export const setParentUrl = createAction<string>(
  WorkflowsActions.SET_PARENT_URL,
);

export const setStepContextVariables = createAction<{
  contextVariables: ContextVariables;
  index: number;
}>(WorkflowsActions.SET_STEP_CONTEXT_VARIABLES);

export const setLogRocketInitialized = createAction<boolean>(
  WorkflowsActions.SET_SHOULD_INIT_LOG_ROCKET,
);

export const resetPreviewWidgetConfig = createAction<boolean | undefined>(
  WorkflowsActions.RESET_PREVIEW_WIDGET_CONFIG,
);

export const setWidgetMode = createAction<WidgetMode>(
  WorkflowsActions.SET_WIDGET_MODE,
);

export const configWidget = createAppAsyncThunk<
  WorkflowConfigResponse & {
    initialQuery?: string;
    isAutonomousAgent: boolean;
  },
  { initialQuery?: string } | void,
  { rejectValue: AttemptedToFetchRedactedConversationError }
>(
  WorkflowsActions.CONFIG_WIDGET,
  async ({ initialQuery } = {}, { getState, rejectWithValue }) => {
    const state = getState();
    const isAutonomousAgent = selectIsAutonomousAgent(state);

    const rawParams = selectRawParams(state);
    const { parentUrl } = state.workflows;
    const intentId = rawParams?.['data-intent-id'] as string | null | undefined;

    const interactiveEmailConversationId = rawParams?.[
      'data-interactive-email-conversation-id'
    ] as string | null | undefined;
    /**
     * - For interactive email workflows, use the conversation passed in by param.
     * - Otherwise use persisted conversationId if it exists.
     * - Otherwise the server will create a new conversation.
     */
    const initialConversationId =
      interactiveEmailConversationId ||
      state.workflows?.conversationId ||
      undefined;

    try {
      const data = await ApiService.request('widget-config', {
        data: {
          conversation_id: initialConversationId,
          parent_url: parentUrl, // save for debug purpose
          raw_params: rawParams,
        },
      });

      return {
        ...data,
        initialQuery,
        intentId,
        isAutonomousAgent,
      };
    } catch (error) {
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isAttemptedToFetchRedactedConversationError(errorData)) {
          if (interactiveEmailConversationId) {
            postMessage({
              event: 'reloadParentPage',
            });
          }
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const reloadTranslatedWidgetConfig = createAppAsyncThunk<
  WorkflowConfigArgs,
  void,
  { rejectValue: AttemptedToFetchRedactedConversationError }
>(
  WorkflowsActions.RELOAD_TRANSLATED_WIDGET_CONFIG,
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState();
    const rawParams = selectRawParams(state);
    const intentId = rawParams?.['data-intent-id'] as string | null | undefined;
    const { parentUrl } = state.workflows;

    try {
      const data = await ApiService.request('widget-config', {
        data: {
          conversation_id: state.workflows?.conversationId || undefined,
          parent_url: parentUrl,
          raw_params: rawParams,
        },
      });

      return {
        ...data,
        intentId,
      };
    } catch (error) {
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isAttemptedToFetchRedactedConversationError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const restartConversation = createAppAsyncThunk<
  InitConversationInterface,
  void,
  { rejectValue: AttemptedToFetchRedactedConversationError }
>(
  WorkflowsActions.RESTART_CONVERSATION,
  async (_: void, { getState, rejectWithValue }) => {
    const state = getState();
    const conversationId = state.workflows.conversationId;
    const nextStepId = state.workflows.nextStepId;
    const transcriptLength = state.workflows.transcriptLength;
    const windowUpdatedContextVariables =
      selectWindowUpdatedContextVariables(state);

    try {
      const data = await ApiService.request('restart-conversation', {
        data: {
          context_variables: windowUpdatedContextVariables,
          next_step_id: nextStepId,
          transcript_length: transcriptLength,
        },
        urlValues: { conversationId },
      });

      return data;
    } catch (error) {
      if (error instanceof CustomFetchError) {
        const errorData = error.data;

        if (isAttemptedToFetchRedactedConversationError(errorData)) {
          return rejectWithValue(errorData);
        }
      }

      return rejectWithCustomFetchError(error, rejectWithValue);
    }
  },
);

export const sendTrackingEvent = createAppAsyncThunk(
  WorkflowsActions.SEND_TRACKING_EVENT,
  async (eventData: SendTrackingEventParameters, { getState }) => {
    const state = getState();
    const isBuilderPreview = state.workflows.isBuilderPreview;
    const isSolveConfigPreview = state.workflows.isSolveConfigPreview;
    const sessionId = state.workflows.sessionId;
    const workflowsOrgConfig = state.workflows.workflowsConfig;
    const persistedConversationId = state.workflows.conversationId;
    const isMobileSDK = selectIsMobileSDK(state);

    const conversationId =
      persistedConversationId || workflowsOrgConfig?.conversation_id;

    const uap = new UAParser();
    const { browser, device, os } = uap.getResult();

    const eventDataWithUserAgentData = {
      ...eventData,
      browser_name: browser.name ?? '',

      /**
       * Device type can be empty string if no device is detected on UserAgent,
       * There is no good way to identify desktop devices through the parser package
       * https://github.com/faisalman/ua-parser-js/issues/182
       */
      device_type: device.type || 'desktop',

      os: os.name ?? '',
      solve_ui_type: isMobileSDK ? 'sdk_webview' : 'web',
      user_agent: window.navigator.userAgent,
    };

    const rawParams = selectRawParams(state);

    if (rawParams?.['emit-tracking-events'] === 'true') {
      postMessage({
        conversation_id: conversationId,
        event: 'forethoughtWidgetTrackingEvent',
        ...eventDataWithUserAgentData,
      });
    }

    sendHandoffErrorToSentry(eventData, conversationId);

    try {
      await ApiService.request('tracking-events', {
        data: {
          channel:
            isBuilderPreview || isSolveConfigPreview ? 'preview' : 'live',
          product: 'flamethrower',
          ...(typeof conversationId === 'string'
            ? { conversation_id: conversationId }
            : {}),
          ...(typeof sessionId === 'number' ? { session_id: sessionId } : {}),
          ...eventDataWithUserAgentData,
        },
      });
    } catch (error) {
      // Ignore errors that we can do nothing about, rethrow other errors:
      if (!isIgnorableNetworkError(error)) {
        // DO NOT use `rejectWithCustomFetchError()` here to avoid infinite loop:
        throw error;
      }
    }
  },
  {
    condition: (_, { getState }) => {
      const state = getState();

      // Abort this request if current browser tab is not the primary tab to
      // prevent duplicate tracking events
      if (!selectIsCurrentTabPrimary(state)) {
        return false;
      }
    },
  },
);

export const submitInteractiveEmailDeflectionFeedback = async ({
  conversationId,
  isDeflected,
  token,
}: {
  conversationId: string;
  isDeflected: boolean;
  token: string | boolean | undefined;
}) => {
  if (!token) {
    const error = new Error(
      'Could not submit deflection feedback. Token is missing or malformed',
    );
    throw error;
  }

  const data = await ApiService.request(
    'interactive-email-deflection-feedback',
    {
      data: {
        is_deflected: isDeflected,
        token: String(token),
      },
      urlValues: { conversationId },
    },
  );

  return data;
};

export const getPresignedUrlToS3 = async (
  conversationId: string,
  fileName: string,
  fileSize: number,
  should_generate_download_link = false,
) => {
  const response = await ApiService.request('upload-file', {
    data: {
      content_length: fileSize,
      conversation_id: conversationId,
      file_name: encodeURIComponent(fileName),
      should_generate_download_link: should_generate_download_link,
    },
  });

  return {
    downloadUrl: response.temp_file_download_link,
    type: response.content_type,
    url: response.temp_file_upload_link,
  };
};

function sendHandoffErrorToSentry(
  eventData: ReadonlyDeep<SendTrackingEventParameters>,
  conversationId: string | undefined,
) {
  if (
    eventData.event_type === 'widget-customer-handoff' &&
    !eventData.success &&
    !sentryHandoffErrorIgnoreList.some(ignoredError =>
      eventData.error?.startsWith(ignoredError),
    )
  ) {
    const redactedEventData = omit(eventData, {
      additionalParameters: undefined,
      conversationProperties: undefined,
      customFields: undefined,
      email: undefined,
      firstName: undefined,
      hiddenFields: undefined,
      lastName: undefined,
      name: undefined,
      question: undefined,
      userid: undefined,
    });

    const scope = getCurrentScope();

    scope.setTag('error', redactedEventData.error);
    scope.setTag('integration', redactedEventData.integration);
    scope.setTag('conversationId', conversationId);
    scope.setTag('solve-origin', ApiService.headers['solve-origin']);

    scope.setContext('context', {
      logRocketSessionUrl: lazyLogRocket.sessionURL,
      ...redactedEventData,
    });

    scope.setFingerprint(['handoff-error']);

    captureException(new Error('Handoff error'));
  }
}
